import React, { useEffect, useState, useRef } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "./firebase/config";
import { ClipLoader } from "react-spinners";
import { FiEdit3, FiTrash2 } from "react-icons/fi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import { getSocialAccountKey } from "./utils/socialChannels";
import {
  getProductImageAccountLabel,
  getProductImageSelectedAccounts,
  normalizeProductImagesPlatform,
} from "./utils/productImagesSocialSettings";
import { removeProductImageConfigWithCronIndex } from "./utils/rtdbProductImagesCronIndex";

const ProductImagesManagerScreen = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFanpage, setSelectedFanpage] = useState("all");
  const [fanpages, setFanpages] = useState([]);
  const [groupedProducts, setGroupedProducts] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // "all", "active", "inactive"
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);

  // States cho chức năng xóa hàng loạt
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Check if we should navigate to edit screen after creating a copy
  useEffect(() => {
    if (location.state?.shouldNavigateToEdit && location.state?.productData) {
      // Navigate to edit screen with the new product data
      navigate("/add-list-images", {
        state: {
          product: location.state.productData,
        },
        replace: true,
      });
    }
  }, [location.state, navigate]);

  const getProductAccounts = (product) => getProductImageSelectedAccounts(product);

  // Hàm nhóm sản phẩm theo tài khoản đăng bài, hỗ trợ cả Facebook legacy và Zalo.
  const groupProductsByFanpage = (productList) => {
    const grouped = {};
    const uniqueFanpagesMap = new Map();

    productList.forEach((product) => {
      getProductAccounts(product).forEach((account) => {
        const accountKey = getSocialAccountKey(account);
        if (!accountKey) return;

        const normalizedPlatform = normalizeProductImagesPlatform(
          account.platform || product.platform
        );
        const normalizedAccount = {
          ...account,
          platform: normalizedPlatform,
          filterKey: accountKey,
        };

        if (!uniqueFanpagesMap.has(accountKey)) {
          uniqueFanpagesMap.set(accountKey, normalizedAccount);
        }

        if (!grouped[accountKey]) {
          grouped[accountKey] = {
            fanpage: normalizedAccount,
            products: [],
          };
        }

        grouped[accountKey].products.push(product);
      });
    });

    return { grouped, uniqueFanpagesMap };
  };

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    const productsRef = ref(db, `productImages/${userId}`);

    const unsubscribe = onValue(productsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const productList = Object.values(data);

        // Nhóm sản phẩm theo fanpage
        const { grouped, uniqueFanpagesMap } =
          groupProductsByFanpage(productList);

        setProducts(productList);
        setGroupedProducts(grouped);
        setFanpages(Array.from(uniqueFanpagesMap.values()));
      } else {
        setProducts([]);
        setGroupedProducts({});
        setFanpages([]);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleFanpageFilter = (fanpageId) => {
    setSelectedFanpage(fanpageId);
    setIsDropdownOpen(false);
  };

  const handleProductClick = (product) => {
    navigate("/add-photo-list", {
      state: { product },
    });
  };

  // Hàm xử lý middle-click để mở trong tab mới
  const handleProductMiddleClick = (e, product) => {
    // e.button === 1 là nút giữa (con lăn chuột)
    if (e.button === 1) {
      e.preventDefault();
      // Mở trong tab mới với state được serialize
      const productData = encodeURIComponent(JSON.stringify(product));
      window.open(`/add-photo-list?product=${productData}`, '_blank');
    }
  };

  const productMatchesSearchTerm = (product) => {
    const normalizedSearchTerm = searchTerm.toLowerCase();
    return (
      product.name?.toLowerCase().includes(normalizedSearchTerm) ||
      getProductAccounts(product).some((account) =>
        account.name?.toLowerCase().includes(normalizedSearchTerm)
      )
    );
  };

  const productMatchesAccountFilter = (product) =>
    selectedFanpage === "all"
      ? true
      : getProductAccounts(product).some(
          (account) => getSocialAccountKey(account) === selectedFanpage
        );

  // Hàm lọc sản phẩm theo tìm kiếm và trạng thái cho từng fanpage
  const getFilteredProductsForFanpage = (fanpageProducts) => {
    return fanpageProducts.filter((product) => {
      const matchesSearch = productMatchesSearchTerm(product);

      const matchesStatus =
        filterStatus === "all"
          ? true
          : filterStatus === "active"
          ? product.autoPostEnabled
          : !product.autoPostEnabled;

      const matchesFanpage = productMatchesAccountFilter(product);

      return matchesSearch && matchesStatus && matchesFanpage;
    });
  };

  // Hàm xử lý chọn sản phẩm
  const handleSelectProduct = (productId) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  // Hàm xử lý chọn tất cả sản phẩm của fanpage
  const handleSelectFanpage = (fanpageId, fanpageProducts) => {
    const newSelected = new Set(selectedProducts);
    const allSelected = fanpageProducts.every(p => selectedProducts.has(p.id));
    
    if (allSelected) {
      // Bỏ chọn tất cả sản phẩm của fanpage này
      fanpageProducts.forEach(p => newSelected.delete(p.id));
    } else {
      // Chọn tất cả sản phẩm của fanpage này
      fanpageProducts.forEach(p => newSelected.add(p.id));
    }
    
    setSelectedProducts(newSelected);
  };

  // Hàm bật/tắt chế độ chỉnh sửa
  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    if (isEditMode) {
      setSelectedProducts(new Set());
    }
  };

  // Hàm xóa sản phẩm đã chọn
  const handleDeleteSelectedProducts = async () => {
    try {
      const userId = localStorage.getItem("userId");
      const deletePromises = Array.from(selectedProducts).map((productId) =>
        removeProductImageConfigWithCronIndex(db, userId, productId)
      );
      
      await Promise.all(deletePromises);
      
      toast.success(`Đã xóa ${selectedProducts.size} sản phẩm thành công!`);
      setSelectedProducts(new Set());
      setShowDeleteConfirm(false);
      setIsEditMode(false);
    } catch (error) {
      console.error("Lỗi khi xóa sản phẩm:", error);
      toast.error("Có lỗi xảy ra khi xóa sản phẩm!");
    }
  };

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setIsStatusDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-4 md:px-8 md:py-8">
        <div className="flex justify-between items-center mb-4 sm:mb-6 md:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 text-center flex-1">
            Quản Lý Danh Sách Ảnh/Video
          </h1>
          <div className="flex gap-2">
            <button
              onClick={toggleEditMode}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                isEditMode
                  ? "bg-gray-500 hover:bg-gray-600 text-white"
                  : "bg-blue-500 hover:bg-blue-600 text-white"
              }`}
            >
              <FiEdit3 className="inline mr-2" />
              {isEditMode ? "Hủy chỉnh sửa" : "Chỉnh sửa"}
            </button>
            {isEditMode && selectedProducts.size > 0 && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
              >
                <FiTrash2 className="inline mr-2" />
                Xóa ({selectedProducts.size})
              </button>
            )}
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="mb-6 space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 md:flex md:flex-row md:gap-4">
          <div className="sm:col-span-2 md:flex-1">
            <input
              type="text"
              placeholder="Tìm kiếm theo tên sản phẩm hoặc tên tài khoản..."
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Status Dropdown */}
          <div className="md:w-40 relative" ref={statusDropdownRef}>
            <button
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              className={`w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center ${
                filterStatus !== "all" ? "bg-white" : "bg-gray-50"
              }`}
            >
              <span>
                {filterStatus === "all"
                  ? "Trạng thái"
                  : filterStatus === "active"
                  ? "Đang chạy"
                  : "Đã tắt"}
              </span>
              <svg
                className={`w-4 h-4 transition-transform ${
                  isStatusDropdownOpen ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            {isStatusDropdownOpen && (
              <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-2">
                <button
                  onClick={() => {
                    setFilterStatus("all");
                    setIsStatusDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Trạng thái
                </button>
                <button
                  onClick={() => {
                    setFilterStatus("active");
                    setIsStatusDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Đang chạy
                </button>
                <button
                  onClick={() => {
                    setFilterStatus("inactive");
                    setIsStatusDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Đã tắt
                </button>
              </div>
            )}
          </div>

          {/* Account Dropdown */}
          <div className="md:w-40 relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center ${
                selectedFanpage !== "all" ? "bg-white" : "bg-gray-50"
              }`}
            >
              <span className="truncate">
                {selectedFanpage === "all"
                  ? "Chọn tài khoản"
                  : fanpages.find((page) => page.filterKey === selectedFanpage)
                      ?.name}
              </span>
              <svg
                className={`w-4 h-4 transition-transform ${
                  isDropdownOpen ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            {isDropdownOpen && (
              <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-2">
                <button
                  onClick={() => handleFanpageFilter("all")}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Chọn tài khoản
                </button>
                {fanpages.map((page) => (
                  <button
                    key={page.filterKey}
                    onClick={() => handleFanpageFilter(page.filterKey)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 truncate"
                    title={page.name}
                  >
                    {page.name} ({getProductImageAccountLabel(page)})
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center">
            <ClipLoader size={50} color={"#123abc"} />
          </div>
        ) : selectedFanpage === "all" ? (
          // Hiển thị theo nhóm fanpage khi chọn "Tất cả"
          Object.keys(groupedProducts).length === 0 ||
          Object.entries(groupedProducts).every(([fanpageId, group]) => {
            const filteredProducts = getFilteredProductsForFanpage(
              group.products
            );
            return filteredProducts.length === 0;
          }) ? (
            <div className="text-center text-gray-500 py-8">
              {products.length === 0
                ? "Không có sản phẩm nào được tạo."
                : "Không tìm thấy kết quả phù hợp với điều kiện tìm kiếm"}
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedProducts)
                .filter(([fanpageId, group]) => {
                  const filteredProducts = getFilteredProductsForFanpage(
                    group.products
                  );
                  return filteredProducts.length > 0;
                })
                .map(([fanpageId, group]) => {
                  const filteredProducts = getFilteredProductsForFanpage(
                    group.products
                  );

                  return (
                    <div
                      key={fanpageId}
                      className="bg-white rounded-lg shadow-md overflow-hidden"
                    >
                      {/* Header của nhóm fanpage */}
                      <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center text-white">
                            {isEditMode && filteredProducts.length > 0 && (
                              <div className="mr-3">
                                <input
                                  type="checkbox"
                                  checked={filteredProducts.length > 0 && filteredProducts.every(p => selectedProducts.has(p.id))}
                                  onChange={() => handleSelectFanpage(fanpageId, filteredProducts)}
                                  className="w-5 h-5 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                />
                              </div>
                            )}
                            <span className="mr-3 inline-flex h-7 min-w-7 items-center justify-center rounded-full bg-white/20 px-2 text-xs font-bold text-white">
                              {getProductImageAccountLabel(group.fanpage)}
                            </span>
                            <div>
                              <h3 className="text-lg font-semibold">
                                {group.fanpage.name}
                              </h3>
                              <p className="text-blue-100 text-sm">
                                {filteredProducts.length} sản phẩm
                                {filteredProducts.length !==
                                  group.products.length &&
                                  ` (từ ${group.products.length} sản phẩm)`}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Nội dung sản phẩm */}
                      <div className="p-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                          {filteredProducts.map((product) => {
                            // Helper logic to merge and sort media
                            const allMedia = [];
                            const images = product.images || {};
                            const videos = product.videos || {};

                            Object.keys(images).forEach((key) => {
                              allMedia.push({ ...images[key], type: "image" });
                            });

                            Object.keys(videos).forEach((key) => {
                              allMedia.push({ ...videos[key], type: "video" });
                            });

                            // Sort by position
                            allMedia.sort((a, b) => {
                              const posA =
                                a.position !== undefined
                                  ? a.position
                                  : Number.MAX_SAFE_INTEGER;
                              const posB =
                                b.position !== undefined
                                  ? b.position
                                  : Number.MAX_SAFE_INTEGER;
                              return posA - posB;
                            });

                            // Calculate stats
                            const totalCount = allMedia.length;
                            const postedCount = allMedia.filter(
                              (m) => m.postStatus === true
                            ).length;
                            const allPosted =
                              totalCount > 0 && postedCount === totalCount;
                            const imageCount = Object.keys(images).length;
                            const videoCount = Object.keys(videos).length;

                            return (
                              <div
                                key={product.id}
                                className={`group bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 ease-out relative overflow-hidden flex flex-col ${
                                  isEditMode && selectedProducts.has(product.id)
                                    ? "ring-2 ring-blue-500 bg-blue-50/30"
                                    : ""
                                }`}
                                onClick={(e) => {
                                  if (isEditMode) {
                                    e.stopPropagation();
                                    handleSelectProduct(product.id);
                                  } else {
                                    handleProductClick(product);
                                  }
                                }}
                                onAuxClick={(e) => {
                                  if (!isEditMode) {
                                    handleProductMiddleClick(e, product);
                                  }
                                }}
                              >
                                {isEditMode && (
                                  <div className="absolute top-3 left-3 z-20">
                                    <div className="bg-white rounded-full p-1 shadow-sm">
                                      <input
                                        type="checkbox"
                                        checked={selectedProducts.has(product.id)}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleSelectProduct(product.id);
                                        }}
                                        className="w-5 h-5 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Status Badge - Top Right */}
                                <div className="absolute top-3 right-3 z-20 flex flex-col items-end gap-2">
                                   {allPosted && (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-500 text-white shadow-sm backdrop-blur-sm">
                                      <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                      </svg>
                                      Đã xong
                                    </span>
                                  )}
                                </div>

                                {/* Content Padding */}
                                <div className="p-5 flex-1 flex flex-col">
                                  {/* Title */}
                                  <div className="mb-4">
                                     <h2 className="text-lg font-bold text-gray-900 leading-tight line-clamp-2 min-h-[3.25rem] group-hover:text-blue-600 transition-colors">
                                      {product.name}
                                    </h2>
                                  </div>
                                  
                                  {/* Thumbnails Grid */}
                                  <div className="grid grid-cols-2 gap-2 mb-5 rounded-lg overflow-hidden border border-gray-100">
                                    {allMedia.length > 0 ? (
                                      allMedia.slice(0, 2).map((media, index) => (
                                        <div key={media.id || index} className="relative aspect-square bg-gray-100 group-inner">
                                          {media.type === "video" ? (
                                            <div className="w-full h-full flex items-center justify-center relative overflow-hidden bg-gray-900">
                                                <video 
                                                    src={media.url} 
                                                    className="w-full h-full object-cover opacity-70 group-inner-hover:opacity-60 transition-opacity"
                                                    preload="metadata"
                                                    muted
                                                />
                                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg border border-white/30">
                                                    <svg className="w-5 h-5 text-white fill-current" viewBox="0 0 24 24">
                                                      <path d="M8 5v14l11-7z" />
                                                    </svg>
                                                </div>
                                              </div>
                                              {/* Video Badge */}
                                              <span className="absolute bottom-2 left-2 text-[10px] font-bold text-white bg-black/60 px-1.5 py-0.5 rounded backdrop-blur-sm">
                                                VIDEO
                                              </span>
                                            </div>
                                          ) : (
                                            <div className="w-full h-full relative">
                                                <img
                                                  src={media.url}
                                                  alt={`Product item`}
                                                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                                />
                                            </div>
                                          )}
                                          
                                          {/* Posted Status Overlay */}
                                          {media.postStatus && (
                                            <div className="absolute bottom-2 right-2 z-10">
                                              <div className="bg-green-500 text-white px-2 py-1 rounded-full shadow-sm text-xs font-medium flex items-center">
                                                <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                </svg>
                                                Đã đăng
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      ))
                                    ) : (
                                      <div className="col-span-2 aspect-[2/1] bg-gray-50 rounded flex items-center justify-center text-gray-400 border border-dashed border-gray-300">
                                        <span className="text-sm">Chưa có media</span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Meta Info */}
                                  <div className="mt-auto space-y-3">
                                    {/* Stats Row */}
                                    <div className="flex items-center justify-between text-sm text-gray-500">
                                        <div className="flex items-center space-x-3">
                                            <span className="flex items-center" title="Total Media">
                                                <svg className="w-4 h-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                                <span className="font-medium text-gray-700">{totalCount}</span>
                                            </span>
                                            {videoCount > 0 && (
                                                <span className="flex items-center" title="Videos">
                                                    <svg className="w-4 h-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                    </svg>
                                                    <span className="font-medium text-gray-700">{videoCount}</span>
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center" title="Posted">
                                             <svg className="w-4 h-4 mr-1.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span className="font-medium text-gray-700">{postedCount}/{totalCount}</span>
                                        </div>
                                    </div>
                                    
                                     {/* Footer Row */}
                                    <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                                        {/* Auto Post Status */}
                                        <div className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                                            product.autoPostEnabled 
                                                ? "bg-blue-50 text-blue-700 border border-blue-100" 
                                                : "bg-gray-100 text-gray-600 border border-gray-200"
                                        }`}>
                                            <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                                                product.autoPostEnabled ? "bg-blue-500" : "bg-gray-400"
                                            }`}></span>
                                            {product.autoPostEnabled ? "Đang chạy" : "Đã tắt"}
                                        </div>

                                        {/* Time Schedule */}
                                        {product.autoPostEnabled && product.postTime && (
                                          <div className="flex items-center text-xs text-gray-500 font-medium font-mono bg-gray-50 px-2 py-0.5 rounded border border-gray-200">
                                            <svg className="w-3 h-3 mr-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            {product.postTime}
                                          </div>
                                        )}
                                    </div>

                                    {/* Accounts */}
                                    {getProductAccounts(product).length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {getProductAccounts(product).slice(0, 2).map((page) => (
                                                <span key={getSocialAccountKey(page)} className="text-[10px] text-gray-500 bg-gray-50 border border-gray-100 px-1.5 py-0.5 rounded truncate" style={{ maxWidth: '50%' }} title={page.name}>
                                                    {page.name}
                                                </span>
                                            ))}
                                            {getProductAccounts(product).length > 2 && (
                                                <span className="text-[10px] text-gray-400 px-1">+{getProductAccounts(product).length - 2}</span>
                                            )}
                                        </div>
                                    )}

                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )
        ) : (
          // Hiển thị sản phẩm của fanpage cụ thể được chọn
          (() => {
            const filteredProducts = products.filter((product) => {
              const matchesSearch = productMatchesSearchTerm(product);

              const matchesStatus =
                filterStatus === "all"
                  ? true
                  : filterStatus === "active"
                  ? product.autoPostEnabled
                  : !product.autoPostEnabled;

              const matchesFanpage = productMatchesAccountFilter(product);

              return matchesSearch && matchesStatus && matchesFanpage;
            });

            return filteredProducts.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                Không tìm thấy kết quả phù hợp với điều kiện tìm kiếm
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {filteredProducts.map((product) => {
                  // Helper logic to merge and sort media
                  const allMedia = [];
                  const images = product.images || {};
                  const videos = product.videos || {};

                  Object.keys(images).forEach((key) => {
                    allMedia.push({ ...images[key], type: "image" });
                  });

                  Object.keys(videos).forEach((key) => {
                    allMedia.push({ ...videos[key], type: "video" });
                  });

                  // Sort by position
                  allMedia.sort((a, b) => {
                    const posA =
                      a.position !== undefined ? a.position : Number.MAX_SAFE_INTEGER;
                    const posB =
                      b.position !== undefined ? b.position : Number.MAX_SAFE_INTEGER;
                    return posA - posB;
                  });

                  // Calculate stats
                  const totalCount = allMedia.length;
                  const postedCount = allMedia.filter(
                    (m) => m.postStatus === true
                  ).length;
                  const allPosted = totalCount > 0 && postedCount === totalCount;
                  const imageCount = Object.keys(images).length;
                  const videoCount = Object.keys(videos).length;

                  return (
                    <div
                      key={product.id}
                      className={`group bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 ease-out relative overflow-hidden flex flex-col ${
                        isEditMode && selectedProducts.has(product.id)
                          ? "ring-2 ring-blue-500 bg-blue-50/30"
                          : ""
                      }`}
                      onClick={(e) => {
                        if (isEditMode) {
                          e.stopPropagation();
                          handleSelectProduct(product.id);
                        } else {
                          handleProductClick(product);
                        }
                      }}
                      onAuxClick={(e) => {
                        if (!isEditMode) {
                          handleProductMiddleClick(e, product);
                        }
                      }}
                    >
                      {isEditMode && (
                        <div className="absolute top-3 left-3 z-20">
                          <div className="bg-white rounded-full p-1 shadow-sm">
                            <input
                              type="checkbox"
                              checked={selectedProducts.has(product.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleSelectProduct(product.id);
                              }}
                              className="w-5 h-5 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                            />
                          </div>
                        </div>
                      )}

                      {/* Status Badge - Top Right */}
                      <div className="absolute top-3 right-3 z-20 flex flex-col items-end gap-2">
                          {allPosted && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-500 text-white shadow-sm backdrop-blur-sm">
                            <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                            Đã xong
                          </span>
                        )}
                      </div>

                      {/* Content Padding */}
                      <div className="p-5 flex-1 flex flex-col">
                        {/* Title */}
                        <div className="mb-4">
                            <h2 className="text-lg font-bold text-gray-900 leading-tight line-clamp-2 min-h-[3.25rem] group-hover:text-blue-600 transition-colors">
                            {product.name}
                          </h2>
                        </div>
                        
                        {/* Thumbnails Grid */}
                        <div className="grid grid-cols-2 gap-2 mb-5 rounded-lg overflow-hidden border border-gray-100">
                          {allMedia.length > 0 ? (
                            allMedia.slice(0, 2).map((media, index) => (
                              <div key={media.id || index} className="relative aspect-square bg-gray-100 group-inner">
                                {media.type === "video" ? (
                                  <div className="w-full h-full flex items-center justify-center relative overflow-hidden bg-gray-900">
                                      <video 
                                          src={media.url} 
                                          className="w-full h-full object-cover opacity-70 group-inner-hover:opacity-60 transition-opacity"
                                          preload="metadata"
                                          muted
                                      />
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                      <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg border border-white/30">
                                          <svg className="w-5 h-5 text-white fill-current" viewBox="0 0 24 24">
                                            <path d="M8 5v14l11-7z" />
                                          </svg>
                                      </div>
                                    </div>
                                    {/* Video Badge */}
                                    <span className="absolute bottom-2 left-2 text-[10px] font-bold text-white bg-black/60 px-1.5 py-0.5 rounded backdrop-blur-sm">
                                      VIDEO
                                    </span>
                                  </div>
                                ) : (
                                  <div className="w-full h-full relative">
                                      <img
                                        src={media.url}
                                        alt={`Product item`}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                      />
                                  </div>
                                )}
                                
                                {/* Posted Status Overlay */}
                                {media.postStatus && (
                                  <div className="absolute bottom-2 right-2 z-10">
                                    <div className="bg-green-500 text-white px-2 py-1 rounded-full shadow-sm text-xs font-medium flex items-center">
                                      <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                      </svg>
                                      Đã đăng
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="col-span-2 aspect-[2/1] bg-gray-50 rounded flex items-center justify-center text-gray-400 border border-dashed border-gray-300">
                              <span className="text-sm">Chưa có media</span>
                            </div>
                          )}
                        </div>

                        {/* Meta Info */}
                        <div className="mt-auto space-y-3">
                          {/* Stats Row */}
                          <div className="flex items-center justify-between text-sm text-gray-500">
                              <div className="flex items-center space-x-3">
                                  <span className="flex items-center" title="Total Media">
                                      <svg className="w-4 h-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                      <span className="font-medium text-gray-700">{totalCount}</span>
                                  </span>
                                  {videoCount > 0 && (
                                      <span className="flex items-center" title="Videos">
                                          <svg className="w-4 h-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                          </svg>
                                          <span className="font-medium text-gray-700">{videoCount}</span>
                                      </span>
                                  )}
                              </div>
                              <div className="flex items-center" title="Posted">
                                    <svg className="w-4 h-4 mr-1.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span className="font-medium text-gray-700">{postedCount}/{totalCount}</span>
                              </div>
                          </div>
                          
                            {/* Footer Row */}
                          <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                              {/* Auto Post Status */}
                              <div className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                                  product.autoPostEnabled 
                                      ? "bg-blue-50 text-blue-700 border border-blue-100" 
                                      : "bg-gray-100 text-gray-600 border border-gray-200"
                              }`}>
                                  <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                                      product.autoPostEnabled ? "bg-blue-500" : "bg-gray-400"
                                  }`}></span>
                                  {product.autoPostEnabled ? "Đang chạy" : "Đã tắt"}
                              </div>

                              {/* Time Schedule */}
                              {product.autoPostEnabled && product.postTime && (
                                <div className="flex items-center text-xs text-gray-500 font-medium font-mono bg-gray-50 px-2 py-0.5 rounded border border-gray-200">
                                  <svg className="w-3 h-3 mr-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  {product.postTime}
                                </div>
                              )}
                          </div>

                          {/* Accounts */}
                          {getProductAccounts(product).length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                  {getProductAccounts(product).slice(0, 2).map((page) => (
                                      <span key={getSocialAccountKey(page)} className="text-[10px] text-gray-500 bg-gray-50 border border-gray-100 px-1.5 py-0.5 rounded truncate" style={{ maxWidth: '50%' }} title={page.name}>
                                          {page.name}
                                      </span>
                                  ))}
                                  {getProductAccounts(product).length > 2 && (
                                      <span className="text-[10px] text-gray-400 px-1">+{getProductAccounts(product).length - 2}</span>
                                  )}
                              </div>
                          )}

                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()
        )}
      </div>
      
      {/* Modal xác nhận xóa */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <FiTrash2 className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Xác nhận xóa sản phẩm
                  </h3>
                </div>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-500">
                  Bạn có chắc chắn muốn xóa <span className="font-semibold text-red-600">{selectedProducts.size}</span> sản phẩm đã chọn?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Hành động này không thể hoàn tác và sẽ xóa tất cả ảnh của sản phẩm.
                </p>
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Hủy
                </button>
                <button
                  onClick={handleDeleteSelectedProducts}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Xóa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <ToastContainer />
    </div>
  );
};

export default ProductImagesManagerScreen;
